import React from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Card,
  Badge,
  Image,
  Link,
  Accordion,
  Separator,
} from '@chakra-ui/react';
import { Stat } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import LandingNavbar from '../components/LandingNavbar';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { useAuth } from '../hooks/useAuth';
import { publicAPI } from '../services/api';
import { PublicFramework } from '../types';
import {
  FiShield,
  FiZap,
  FiTrendingUp,
  FiUsers,
  FiFileText,
  FiBarChart,
  FiCheck,
  FiLock,
  FiEye,
  FiArrowRight,
  FiChevronDown,
  FiUpload,
  FiCpu,
  FiClipboard,
  FiTarget,
  FiLayers,
  FiSearch,
} from 'react-icons/fi';

// Motion wrapper for scroll animations
const MotionBox = motion.create(Box as React.FC<any>);

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

type FrameworkCard = {
  short: string;
  name: string;
  version: string;
  description: string;
  tags: string[];
  color: string;
};

type FrameworkTemplate = {
  shortKey: string;
  nameKey: string;
  versionKey: string;
  descriptionKey: string;
  tagKeys: string[];
  color: string;
};

const FALLBACK_FRAMEWORKS: FrameworkTemplate[] = [
  {
    shortKey: 'frameworkIsoShort',
    nameKey: 'frameworkIsoName',
    versionKey: 'frameworkIsoVersion',
    descriptionKey: 'frameworkIsoDescription',
    tagKeys: ['frameworkTagStandard', 'frameworkTagClauses410', 'frameworkTagAnnexAControls'],
    color: 'blue',
  },
  {
    shortKey: 'frameworkSoc2Short',
    nameKey: 'frameworkSoc2Name',
    versionKey: 'frameworkSoc2Version',
    descriptionKey: 'frameworkSoc2Description',
    tagKeys: ['frameworkTagFramework', 'frameworkTagTrustServices', 'frameworkTagSecurity'],
    color: 'green',
  },
  {
    shortKey: 'frameworkNistShort',
    nameKey: 'frameworkNistName',
    versionKey: 'frameworkNistVersion',
    descriptionKey: 'frameworkNistDescription',
    tagKeys: ['frameworkTagFramework', 'frameworkTagIdentify', 'frameworkTagProtect'],
    color: 'purple',
  },
  {
    shortKey: 'frameworkGdprShort',
    nameKey: 'frameworkGdprName',
    versionKey: 'frameworkGdprVersion',
    descriptionKey: 'frameworkGdprDescription',
    tagKeys: ['frameworkTagRegulation', 'frameworkTagDataRights', 'frameworkTagSecurity'],
    color: 'orange',
  },
  {
    shortKey: 'frameworkPciShort',
    nameKey: 'frameworkPciName',
    versionKey: 'frameworkPciVersion',
    descriptionKey: 'frameworkPciDescription',
    tagKeys: ['frameworkTagStandard', 'frameworkTag12Requirements', 'frameworkTagCardholderData'],
    color: 'red',
  },
  {
    shortKey: 'frameworkDgaShort',
    nameKey: 'frameworkDgaName',
    versionKey: 'frameworkDgaVersion',
    descriptionKey: 'frameworkDgaDescription',
    tagKeys: ['frameworkTagRegulation', 'frameworkTagRiskManagement', 'frameworkTagBusinessContinuity'],
    color: 'cyan',
  },
];

const MORE_SOON_FRAMEWORK: FrameworkTemplate = {
  shortKey: 'frameworkMoreSoonShort',
  nameKey: 'frameworkMoreSoonName',
  versionKey: 'frameworkMoreSoonVersion',
  descriptionKey: 'frameworkMoreSoonDescription',
  tagKeys: ['frameworkTagHipaa', 'frameworkTagIso27701', 'frameworkTagDora', 'frameworkTagNis2'],
  color: 'gray',
};

const getFrameworkColor = (standardType: string, classification?: string): string => {
  const type = (standardType || '').toUpperCase();
  if (type.startsWith('ISO')) return 'blue';
  if (type.startsWith('SOC')) return 'green';
  if (type.startsWith('NIST')) return 'purple';
  if (type.startsWith('GDPR')) return 'orange';
  if (type.startsWith('PCI')) return 'red';
  if (type.startsWith('DGA')) return 'cyan';

  const cls = (classification || '').toLowerCase();
  if (cls === 'standard') return 'blue';
  if (cls === 'framework') return 'purple';
  if (cls === 'regulation') return 'orange';
  return 'gray';
};

const getShortLabel = (standardType: string, name: string): string => {
  const type = (standardType || '').toUpperCase();
  if (type.startsWith('ISO')) return 'ISO 27001';
  if (type.startsWith('SOC')) return 'SOC 2';
  if (type.startsWith('NIST')) return 'NIST CSF';
  if (type.startsWith('GDPR')) return 'GDPR';
  if (type.startsWith('PCI')) return 'PCI DSS';
  if (type === 'DGA_RM_BC_2025') return 'DGA RM&BC';
  if (type.startsWith('DGA')) return 'DGA';

  return name
    .split(' ')
    .slice(0, 2)
    .join(' ')
    .toUpperCase();
};

const mapFrameworkToCard = (framework: PublicFramework, t: any): FrameworkCard => {
  const tags: string[] = [];
  if (framework.classification) {
    tags.push(framework.classification);
  }
  if (framework.total_controls > 0) {
    tags.push(t('landing.controlsCount', { count: framework.total_controls }));
  }
  tags.push(framework.standard_type.replace(/_/g, ' '));

  return {
    short: getShortLabel(framework.standard_type, framework.name),
    name: framework.name,
    version: framework.version,
    description: framework.description || t('landing.frameworkDefaultDescription'),
    tags,
    color: getFrameworkColor(framework.standard_type, framework.classification),
  };
};

const LandingPage: React.FC = () => {
  const { t } = useTranslation();
  const bg = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const { isAuthenticated } = useAuth();
  const {
    data: publicFrameworks,
    isLoading: isFrameworksLoading,
    isError: isFrameworksError,
  } = useQuery('public-frameworks', publicAPI.getFrameworks, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const translateFrameworkTemplate = React.useCallback((framework: FrameworkTemplate): FrameworkCard => ({
    short: t(`landing.${framework.shortKey}`),
    name: t(`landing.${framework.nameKey}`),
    version: t(`landing.${framework.versionKey}`),
    description: t(`landing.${framework.descriptionKey}`),
    tags: framework.tagKeys.map((key) => t(`landing.${key}`)),
    color: framework.color,
  }), [t]);

  const frameworkCards = React.useMemo(() => {
    if (publicFrameworks && publicFrameworks.length > 0) {
      return publicFrameworks.map((framework) => mapFrameworkToCard(framework, t));
    }
    return FALLBACK_FRAMEWORKS.map(translateFrameworkTemplate);
  }, [publicFrameworks, t, translateFrameworkTemplate]);

  const features = [
    {
      icon: FiShield,
      title: t('landing.featureAiPoweredComplianceTitle'),
      description: t('landing.featureAiPoweredComplianceDescription'),
      color: 'blue',
    },
    {
      icon: FiZap,
      title: t('landing.featureRealTimeAnalysisTitle'),
      description: t('landing.featureRealTimeAnalysisDescription'),
      color: 'orange',
    },
    {
      icon: FiTrendingUp,
      title: t('landing.featureContinuousMonitoringTitle'),
      description: t('landing.featureContinuousMonitoringDescription'),
      comingSoon: true,
      color: 'green',
    },
    {
      icon: FiUsers,
      title: t('landing.featureTeamCollaborationTitle'),
      description: t('landing.featureTeamCollaborationDescription'),
      comingSoon: true,
      color: 'purple',
    },
    {
      icon: FiFileText,
      title: t('landing.featureMultiFrameworkSupportTitle'),
      description: t('landing.featureMultiFrameworkSupportDescription'),
      color: 'teal',
    },
    {
      icon: FiBarChart,
      title: t('landing.featureDetailedReportingTitle'),
      description: t('landing.featureDetailedReportingDescription'),
      color: 'pink',
    },
    {
      icon: FiTarget,
      title: t('landing.featureFlexibleAssessmentScopeTitle'),
      description: t('landing.featureFlexibleAssessmentScopeDescription'),
      color: 'purple',
    },
    {
      icon: FiLayers,
      title: t('landing.featureMultiDocumentUploadTitle'),
      description: t('landing.featureMultiDocumentUploadDescription'),
      color: 'blue',
    },
    {
      icon: FiSearch,
      title: t('landing.featureTraceablePolicyCitationsTitle'),
      description: t('landing.featureTraceablePolicyCitationsDescription'),
      color: 'orange',
    },
    {
      icon: FiLock,
      title: t('landing.featureEndToEndEncryptionTitle'),
      description: t('landing.featureEndToEndEncryptionDescription'),
      color: 'green',
    },
    {
      icon: FiShield,
      title: t('landing.featurePrivacyFirstProcessingTitle'),
      description: t('landing.featurePrivacyFirstProcessingDescription'),
      color: 'purple',
    },
    {
      icon: FiEye,
      title: t('landing.featureCompleteAuditTrailsTitle'),
      description: t('landing.featureCompleteAuditTrailsDescription'),
      color: 'teal',
    },
  ];

  const pricingPlans = [
    {
      id: 'free',
      name: t('landing.planFreeName'),
      price: '$0',
      period: t('landing.planFreePeriod'),
      description: t('landing.planFreeDescription'),
      features: [
        t('landing.planFeature3Assessments'),
        t('landing.planFeature10MbFile'),
        t('landing.planFeature1Document'),
        t('landing.planFeature15DaysStorage'),
        t('landing.planFeatureComplianceReports'),
        t('landing.planFeatureEmailSupport'),
        t('landing.planFeatureIsoSocFrameworks'),
        t('landing.planFeatureEncryptedStorage')
      ],
      buttonText: t('landing.planFreeButton'),
      popular: false
    },
    {
      id: 'starter',
      name: t('landing.planStarterName'),
      price: '$39',
      period: t('landing.planMonthlyPeriod'),
      yearlyPrice: '$390',
      yearlyPeriod: t('landing.planYearlyPeriod'),
      description: t('landing.planStarterDescription'),
      features: [
        t('landing.planFeature15Assessments'),
        t('landing.planFeature15MbFile'),
        t('landing.planFeature5Documents'),
        t('landing.planFeature60DaysStorage'),
        t('landing.planFeatureComplianceReports'),
        t('landing.planFeatureEmailSupport'),
        t('landing.planFeatureAllFrameworksNamed'),
        t('landing.planFeatureAuditTrails'),
        t('landing.planFeatureEncryptedStorage')
      ],
      buttonText: t('landing.planStarterButton'),
      popular: false
    },
    {
      id: 'pro',
      name: t('landing.planProName'),
      price: '$89',
      period: t('landing.planMonthlyPeriod'),
      yearlyPrice: '$890',
      yearlyPeriod: t('landing.planYearlyPeriod'),
      description: t('landing.planProDescription'),
      features: [
        t('landing.planFeature30Assessments'),
        t('landing.planFeature25MbFile'),
        t('landing.planFeature10Documents'),
        t('landing.planFeature90DaysStorage'),
        t('landing.planFeatureComplianceReports'),
        t('landing.planFeatureEmailSupport'),
        t('landing.planFeatureAllFrameworks'),
        t('landing.planFeatureAuditTrails'),
        t('landing.planFeatureEncryptedStorage')
      ],
      buttonText: t('landing.planProButton'),
      popular: true
    },
    {
      id: 'business',
      name: t('landing.planBusinessName'),
      price: '$199',
      period: t('landing.planMonthlyPeriod'),
      yearlyPrice: '$1,990',
      yearlyPeriod: t('landing.planYearlyPeriod'),
      description: t('landing.planBusinessDescription'),
      features: [
        t('landing.planFeature100Assessments'),
        t('landing.planFeature50MbFile'),
        t('landing.planFeature20Documents'),
        t('landing.planFeature180DaysStorage'),
        t('landing.planFeatureComplianceReports'),
        t('landing.planFeatureEmailSupport'),
        t('landing.planFeatureAllFrameworks'),
        t('landing.planFeatureApiAccess'),
        t('landing.planFeatureAuditTrails'),
        t('landing.planFeatureEncryptedStorage')
      ],
      buttonText: t('landing.planBusinessButton'),
      popular: false
    }
  ];

  const stats = [
    { label: t('landing.statAssessmentsCompletedLabel'), number: '10,000+', help: t('landing.statAssessmentsCompletedHelp') },
    { label: t('landing.statComplianceFrameworksLabel'), number: '5+', help: t('landing.statComplianceFrameworksHelp') },
    { label: t('landing.statCustomerSatisfactionLabel'), number: '98%', help: t('landing.statCustomerSatisfactionHelp') },
    { label: t('landing.statTimeSavedLabel'), number: '95%', help: t('landing.statTimeSavedHelp') },
  ];

  const faqItems = [
    {
      question: t('landing.faqGapAssessmentQuestion'),
      answer: t('landing.faqGapAssessmentAnswer'),
    },
    {
      question: t('landing.faqFrameworksQuestion'),
      answer: t('landing.faqFrameworksAnswer'),
    },
    {
      question: t('landing.faqAccuracyQuestion'),
      answer: t('landing.faqAccuracyAnswer'),
    },
    {
      question: t('landing.faqDataSecureQuestion'),
      answer: t('landing.faqDataSecureAnswer'),
    },
    {
      question: t('landing.faqCertificationQuestion'),
      answer: t('landing.faqCertificationAnswer'),
    },
    {
      question: t('landing.faqFileFormatsQuestion'),
      answer: t('landing.faqFileFormatsAnswer'),
    },
  ];

  return (
    <Box bg={bg}>
      <Helmet>
        <title>{t('landing.metaTitle')}</title>
        <meta name="description" content={t('landing.metaDescription')} />
      </Helmet>

      <LandingNavbar />

      {/* Hero Section */}
      <Box bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white" pt={{ base: 16, md: 24 }} pb={{ base: 12, md: 20 }}>
        <Container maxW="6xl">
          <VStack gap={8} textAlign="center">
            <VStack gap={4}>
              <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }} fontWeight="bold" lineHeight="1.2">
                {t('landing.heroTitlePrefix')}{' '}
                <Text as="span" color="pink.200">
                  {t('landing.heroTitleHighlight')}
                </Text>
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} opacity={0.9} maxW="3xl">
                {t('landing.heroSubtitle')}
              </Text>
            </VStack>

            <HStack gap={4} flexWrap="wrap" justify="center">
              {!isAuthenticated ? (
                <RouterLink to="/register" style={{ textDecoration: 'none' }}>
                  <Button
                    size="lg"
                    bg="white"
                    color="purple.700"
                    _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                    transition="all 0.2s"
                    fontWeight="bold"
                  >
                    {t('landing.startFreeTrial')}
                    <Icon as={FiArrowRight} ml={2} />
                  </Button>
                </RouterLink>
              ) : (
                <RouterLink to="/dashboard" style={{ textDecoration: 'none' }}>
                  <Button
                    size="lg"
                    bg="white"
                    color="purple.700"
                    _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                    transition="all 0.2s"
                    fontWeight="bold"
                  >
                    {t('landing.goToDashboard')}
                    <Icon as={FiArrowRight} ml={2} />
                  </Button>
                </RouterLink>
              )}
              <Link href="#how-it-works" _hover={{ textDecoration: 'none' }}>
                <Button
                  size="lg"
                  variant="outline"
                  borderColor="whiteAlpha.600"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.200', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  {t('landing.seeHowItWorks')}
                </Button>
              </Link>
            </HStack>

            {/* Hero Image */}
            <Box mt={6} maxW="5xl" w="full">
              <Image
                src="/homepageimage2.jpg"
                alt={t('landing.heroImageAlt')}
                borderRadius="xl"
                shadow="2xl"
                w="full"
                h="auto"
                objectFit="contain"
                loading="eager"
                border="1px solid"
                borderColor="whiteAlpha.300"
              />
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Stats Section */}
      <MotionBox
        py={16}
        bg={cardBg}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        variants={staggerContainer}
      >
        <Container maxW="6xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={8}>
            {stats.map((stat, index) => (
              <MotionBox key={index} variants={fadeInUp}>
                <Stat.Root textAlign="center">
                  <Stat.ValueText fontSize="3xl" fontWeight="bold" color="brand.500">
                    {stat.number}
                  </Stat.ValueText>
                  <Stat.Label fontSize="lg" color="gray.600">
                    {stat.label}
                  </Stat.Label>
                  <Stat.HelpText color="gray.500">
                    {stat.help}
                  </Stat.HelpText>
                </Stat.Root>
              </MotionBox>
            ))}
          </SimpleGrid>

        </Container>
      </MotionBox>

      {/* Supported Compliance Frameworks Section */}
      <Box py={20} bg={cardBg} id="standards">
        <Container maxW="6xl">
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <VStack gap={12}>
              <MotionBox variants={fadeInUp} w="full">
                <VStack gap={4} textAlign="center">
                  <Text fontSize="md" color="blue.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                    {t('landing.complianceFrameworksEyebrow')}
                  </Text>
                  <Heading as="h2" size="2xl">
                    {t('landing.supportedFrameworksTitle')}
                  </Heading>
                  <Text fontSize="xl" maxW="2xl" opacity={0.8}>
                    {t('landing.supportedFrameworksDescription')}
                  </Text>
                </VStack>
              </MotionBox>

              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={6} w="full">
                {[...frameworkCards, translateFrameworkTemplate(MORE_SOON_FRAMEWORK)].map((fw, i) => (
                  <MotionBox key={i} variants={fadeInUp}>
                    <Box
                      p={6}
                      bg="white"
                      borderRadius="xl"
                      shadow="md"
                      borderTop="4px solid"
                      borderColor={`${fw.color}.400`}
                      h="full"
                      _hover={{ shadow: 'xl', transform: 'translateY(-4px)' }}
                      transition="all 0.3s"
                      _dark={{ bg: 'gray.800' }}
                    >
                      <VStack align="start" gap={4}>
                        <HStack justify="space-between" w="full" align="start">
                          <Badge
                            colorScheme={fw.color}
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight="bold"
                          >
                            {fw.short}
                          </Badge>
                        </HStack>
                        <Box>
                          <Heading as="h3" size="sm" mb={1}>{fw.name}</Heading>
                          <Text fontSize="xs" color="gray.500" fontStyle="italic">{fw.version}</Text>
                        </Box>
                        <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }} lineHeight="tall">
                          {fw.description}
                        </Text>
                        <HStack flexWrap="wrap" gap={2} pt={1}>
                          {fw.tags.map((t: string) => (
                            <Badge key={t} variant="outline" colorScheme={fw.color} fontSize="xs" borderRadius="full">
                              {t}
                            </Badge>
                          ))}
                        </HStack>
                      </VStack>
                    </Box>
                  </MotionBox>
                ))}
              </SimpleGrid>
              {isFrameworksLoading && (
                <Text fontSize="sm" color="gray.500">
                  {t('landing.loadingFrameworks')}
                </Text>
              )}
              {isFrameworksError && (
                <Text fontSize="sm" color="orange.500">
                  {t('landing.frameworksUnavailable')}
                </Text>
              )}
            </VStack>
          </MotionBox>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={10} id="features">
        <Container maxW="6xl">
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <VStack gap={8}>
              <MotionBox variants={fadeInUp} w="full">
                <VStack gap={3} textAlign="center">
                  <Text fontSize="md" color="blue.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                    {t('landing.featuresEyebrow')}
                  </Text>
                  <Heading as="h2" size="xl">
                    {t('landing.featuresTitle')}
                  </Heading>
                </VStack>
              </MotionBox>

              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={5} w="full">
                {features.map((feature, index) => (
                  <MotionBox key={index} variants={fadeInUp}>
                    <VStack
                      p={5}
                      bg="white"
                      borderRadius="xl"
                      shadow="md"
                      gap={3}
                      align="start"
                      h="full"
                      position="relative"
                      _dark={{ bg: 'gray.800' }}
                      _hover={{ transform: 'translateY(-3px)', shadow: 'lg' }}
                      transition="all 0.25s"
                    >
                      {'comingSoon' in feature && feature.comingSoon && (
                        <Badge
                          position="absolute"
                          top={4}
                          right={4}
                          colorScheme="purple"
                          fontSize="xs"
                          borderRadius="full"
                          px={2}
                        >
                          {t('landing.comingSoon')}
                        </Badge>
                      )}
                      <Box
                        p={2}
                        bg={`${feature.color}.50`}
                        borderRadius="lg"
                        _dark={{ bg: `${feature.color}.900` }}
                      >
                        <Icon as={feature.icon} boxSize={5} color={`${feature.color}.500`} />
                      </Box>
                      <VStack gap={1} align="start">
                        <Heading as="h3" size="sm">{feature.title}</Heading>
                        <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                          {feature.description}
                        </Text>
                      </VStack>
                    </VStack>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </VStack>
          </MotionBox>
        </Container>
      </Box>



      {/* How It Works Section */}
      <Box py={14} bg={cardBg} id="how-it-works">
        <Container maxW="6xl">
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <VStack gap={16}>
              <MotionBox variants={fadeInUp} w="full">
                <VStack gap={4} textAlign="center">
                  <Text fontSize="md" color="blue.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                    {t('landing.howItWorksEyebrow')}
                  </Text>
                  <Heading as="h2" size="xl" fontWeight="bold">
                    {t('landing.howItWorksTitle')}
                  </Heading>
                  <Text fontSize="lg" color="gray.600" maxW="2xl">
                    {t('landing.howItWorksDescription')}
                  </Text>
                </VStack>
              </MotionBox>

              <SimpleGrid columns={{ base: 1, md: 3 }} gap={8}>
                {[
                  {
                    icon: FiUpload,
                    color: 'cyan',
                    step: '01',
                    title: t('landing.howStepUploadTitle'),
                    description: t('landing.howStepUploadDescription'),
                  },
                  {
                    icon: FiCpu,
                    color: 'pink',
                    step: '02',
                    title: t('landing.howStepAnalysisTitle'),
                    description: t('landing.howStepAnalysisDescription'),
                  },
                  {
                    icon: FiClipboard,
                    color: 'purple',
                    step: '03',
                    title: t('landing.howStepReportTitle'),
                    description: t('landing.howStepReportDescription'),
                  },
                ].map((step, index) => (
                  <MotionBox key={index} variants={fadeInUp}>
                    <VStack gap={5} textAlign="center" p={8} bg="white" borderRadius="xl" shadow="md" h="full" _dark={{ bg: 'gray.800' }}>
                      <Box position="relative">
                        <Box
                          w={16}
                          h={16}
                          borderRadius="full"
                          bg={`${step.color}.500`}
                          color="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon as={step.icon} boxSize={7} />
                        </Box>
                        <Text
                          position="absolute"
                          top={-2}
                          right={-2}
                          fontSize="xs"
                          fontWeight="bold"
                          color={`${step.color}.500`}
                          bg={`${step.color}.50`}
                          borderRadius="full"
                          w={6}
                          h={6}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          _dark={{ bg: `${step.color}.900` }}
                        >
                          {step.step}
                        </Text>
                      </Box>
                      <VStack gap={2}>
                        <Heading as="h3" size="md">{step.title}</Heading>
                        <Text color="gray.600" _dark={{ color: 'gray.300' }}>
                          {step.description}
                        </Text>
                      </VStack>
                    </VStack>
                  </MotionBox>
                ))}
              </SimpleGrid>
            </VStack>
          </MotionBox>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box py={14} id="pricing">
        <Container maxW="8xl" px={{ base: 4, md: 8 }}>
          <VStack gap={12}>
            <VStack gap={4} textAlign="center">
              <Text fontSize="md" color="blue.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                {t('landing.pricingEyebrow')}
              </Text>
              <Heading size="2xl">
                {t('landing.pricingTitle')}
              </Heading>
              <Text fontSize="xl" maxW="2xl" opacity={0.8}>
                {t('landing.pricingDescription')}
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} gap={6} w="full">
              {pricingPlans.map((plan, index) => (
                <Card.Root
                  key={index}
                  border={plan.popular ? '2px solid' : '1px solid'}
                  borderColor={plan.popular ? 'blue.500' : 'gray.200'}
                  shadow={plan.popular ? 'xl' : 'md'}
                  position="relative"
                  _dark={{
                    borderColor: plan.popular ? 'blue.400' : 'gray.600',
                    bg: 'gray.800'
                  }}
                >
                  {plan.popular && (
                    <Badge
                      position="absolute"
                      top={-3}
                      left="50%"
                      transform="translateX(-50%)"
                      colorScheme="blue"
                      px={4}
                      py={1}
                      borderRadius="full"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      {t('landing.mostPopular')}
                    </Badge>
                  )}

                  <Card.Body p={6}>
                    <VStack gap={5} align="stretch">
                      <VStack gap={1}>
                        <Text fontSize="md" fontWeight="semibold" color="gray.600" _dark={{ color: 'gray.300' }}>
                          {plan.name}
                        </Text>
                        <VStack gap={0}>
                          <HStack gap={1} justify="center">
                            <Text fontSize="3xl" fontWeight="bold">
                              {plan.price}
                            </Text>
                            <Text fontSize="sm" color="gray.500" alignSelf="flex-end" pb={1}>
                              {plan.period}
                            </Text>
                          </HStack>
                          {plan.yearlyPrice && (
                            <HStack gap={1} justify="center">
                              <Text fontSize="sm" fontWeight="semibold" color="blue.500">
                                {plan.yearlyPrice}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {plan.yearlyPeriod}
                              </Text>
                              <Badge colorScheme="green" fontSize="xs">
                                {t('landing.save17')}
                              </Badge>
                            </HStack>
                          )}
                        </VStack>
                        <Text fontSize="xs" color="gray.500" textAlign="center" _dark={{ color: 'gray.400' }}>
                          {plan.description}
                        </Text>
                      </VStack>

                      <VStack gap={2} align="stretch">
                        {plan.features.map((feature, featureIndex) => (
                          <HStack key={featureIndex} gap={2}>
                            <Icon as={FiCheck} color="green.500" boxSize={3} flexShrink={0} />
                            <Text fontSize="xs">{feature}</Text>
                          </HStack>
                        ))}
                      </VStack>

                      <RouterLink to={`/register?plan=${plan.id}`} style={{ width: '100%', textDecoration: 'none' }}>
                        <Button
                          colorScheme={plan.popular ? 'blue' : 'gray'}
                          size="md"
                          w="full"
                          mt={2}
                        >
                          {plan.buttonText}
                        </Button>
                      </RouterLink>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>

            <Text fontSize="sm" color="gray.500">
              {t('landing.needCustomPlan')} <Link color="blue.500" href="mailto:support@neurev.ai">{t('landing.contactUs')}</Link>
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={20} bg={cardBg} id="faq">
        <Container maxW="3xl">
          <MotionBox
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <VStack gap={10}>
              <MotionBox variants={fadeInUp} w="full">
                <VStack gap={4} textAlign="center">
                  <Text fontSize="md" color="blue.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide">
                    {t('landing.navFaq')}
                  </Text>
                  <Heading as="h2" size="xl">
                    {t('landing.faqTitle')}
                  </Heading>
                </VStack>
              </MotionBox>

              <MotionBox variants={fadeInUp} w="full">
                <Accordion.Root collapsible variant="enclosed">
                  {faqItems.map((item, index) => (
                    <Accordion.Item key={index} value={`faq-${index}`}>
                      <Accordion.ItemTrigger cursor="pointer" p={5}>
                        <Text flex="1" textAlign="left" fontWeight="semibold">
                          {item.question}
                        </Text>
                        <Accordion.ItemIndicator>
                          <Icon as={FiChevronDown} />
                        </Accordion.ItemIndicator>
                      </Accordion.ItemTrigger>
                      <Accordion.ItemContent>
                        <Accordion.ItemBody>
                          <Text color="gray.600" _dark={{ color: 'gray.300' }} pb={2}>
                            {item.answer}
                          </Text>
                        </Accordion.ItemBody>
                      </Accordion.ItemContent>
                    </Accordion.Item>
                  ))}
                </Accordion.Root>
              </MotionBox>
            </VStack>
          </MotionBox>
        </Container>
      </Box>



      {/* CTA Section */}
      <Box py={20} bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" color="white">
        <Container maxW="4xl">
          <VStack gap={8} textAlign="center">
            <Heading as="h2" size="xl" fontWeight="bold">
              {t('landing.ctaTitle')}
            </Heading>
            <Text fontSize="lg" opacity={0.9} maxW="2xl">
              {t('landing.ctaDescription')}
            </Text>
            <HStack gap={4}>
              {!isAuthenticated ? (
                <RouterLink to="/register" style={{ textDecoration: 'none' }}>
                  <Button
                    size="lg"
                    bg="white"
                    color="purple.700"
                    fontWeight="bold"
                    _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                    transition="all 0.2s"
                  >
                    {t('landing.startFreeTrial')}
                    <Icon as={FiArrowRight} ml={2} />
                  </Button>
                </RouterLink>
              ) : (
                <RouterLink to="/dashboard" style={{ textDecoration: 'none' }}>
                  <Button
                    size="lg"
                    bg="white"
                    color="purple.700"
                    fontWeight="bold"
                    _hover={{ bg: 'gray.100', transform: 'translateY(-2px)' }}
                    transition="all 0.2s"
                  >
                    {t('landing.goToDashboard')}
                    <Icon as={FiArrowRight} ml={2} />
                  </Button>
                </RouterLink>
              )}
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box py={16} bg="gray.900" color="white">
        <Container maxW="6xl">
          <SimpleGrid columns={{ base: 1, md: 4 }} gap={8} mb={8}>
            <VStack align="start" gap={4}>
              <Text fontSize="xl" fontWeight="bold">NEUREV</Text>
              <Text color="gray.400" fontSize="sm">
                {t('landing.footerDescription')}
              </Text>
            </VStack>

            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                {t('landing.footerProduct')}
              </Text>
              <Link href="#features" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.navFeatures')}</Link>
              <Link href="#pricing" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.navPricing')}</Link>
              <Link href="#how-it-works" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.footerHowItWorks')}</Link>
              <Link href="#faq" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.navFaq')}</Link>
            </VStack>

            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                {t('landing.footerFrameworks')}
              </Text>
              <Text color="gray.400" fontSize="sm">{t('landing.footerFrameworkIso')}</Text>
              <Text color="gray.400" fontSize="sm">{t('landing.footerFrameworkSoc2')}</Text>
              <Text color="gray.400" fontSize="sm">{t('landing.footerFrameworkNist')}</Text>
              <Text color="gray.400" fontSize="sm">{t('landing.footerFrameworkGdprPci')}</Text>
            </VStack>

            <VStack align="start" gap={3}>
              <Text fontWeight="semibold" fontSize="sm" textTransform="uppercase" letterSpacing="wide">
                {t('landing.footerCompany')}
              </Text>
              <Link href="mailto:support@neurev.ai" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.navContact')}</Link>
              <Link href="/privacy" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.privacyPolicy')}</Link>
              <Link href="/terms" color="gray.400" fontSize="sm" _hover={{ color: 'white' }}>{t('landing.termsOfService')}</Link>
            </VStack>
          </SimpleGrid>

          <Separator borderColor="gray.700" />

          <HStack justify="space-between" mt={8} flexWrap="wrap" gap={4}>
            <Text color="gray.500" fontSize="sm">
              {t('landing.footerCopyright')}
            </Text>
            <Text color="gray.500" fontSize="sm">
              {t('landing.footerTagline')}
            </Text>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage; 
